// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { VERIFY_EMAIL, RESEND_VERIFICATION_CODE } from "lib/constants";
import { putAPICall } from "lib/callApi";
import { useSelector, useDispatch } from "react-redux";
import { CircularProgress, FormHelperText, Stack } from "@mui/material";
import { loginUserAction } from "redux/slice/users";
import OTPInput from "components/OTPInput";
import CoverLayout from "../components/CoverLayout";

function EmailVerification() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [initialValues, setInitialValues] = useState({ otp: null });
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const queryParam = new URLSearchParams(window.location.search);

  const validationSchema = Yup.object({
    otp: Yup.number()
      .typeError("OTP must be a number")
      .positive("OTP must be positive")
      .integer("OTP must be an integer")
      .min(100000, "OTP must be 6 digits")
      .max(999999, "OTP must be 6 digits")
      .required("OTP is required"),
  });

  useEffect(() => {
    if (loginUser?.emailVerified) navigate("/home");
    if (loginUser?.disabled) navigate("/workspace");
  }, [loginUser]);

  useEffect(() => {
    let timer;

    if (disabled) {
      timer = setInterval(() => {
        if (remainingTime === 1) {
          clearInterval(timer);
          setDisabled(false);
          setRemainingTime(30);
        } else {
          setRemainingTime((prevTime) => prevTime - 1);
        }
      }, 1000);
    }

    return () => {
      clearInterval(timer);
    };
  }, [disabled, remainingTime]);

  const onSubmit = async (data) => {
    try {
      const email = queryParam.get("email") || loginUser.email;
      if (!email) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: "Email not found",
          }),
          {
            variant: "custom",
          }
        );
        navigate("/sign-up");
      }
      setIsLoading(true);
      const result = await putAPICall(VERIFY_EMAIL, false, { ...data, email });
      if (result.data.success) {
        dispatch(
          loginUserAction.getLoginUserData({
            ...loginUser,
            email,
            emailVerified: true,
          })
        );
        setIsLoading(false);
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );
        if (loginUser.id) {
          navigate("/home");
        } else {
          navigate("/workspace");
        }
      } else {
        setIsLoading(false);
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    const otp = queryParam.get("otp");
    if (queryParam.get("email") && otp) {
      setInitialValues({ otp });
      onSubmit({ otp });
    }
  }, []);

  const handleResendCode = async () => {
    try {
      if (disabled) return;
      const email = queryParam.get("email") || loginUser.email;
      if (!email) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: "Email not found",
          }),
          {
            variant: "custom",
          }
        );
        navigate("/sign-up");
      }
      setIsLoading(true);
      const result = await putAPICall(RESEND_VERIFICATION_CODE, false, { email });
      if (result.status === 200) {
        setIsLoading(false);
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );
        setDisabled(true);
        setRemainingTime(30);
      } else {
        setIsLoading(false);
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const handleLogout = () => {
    dispatch(loginUserAction.logoutUsersSlice());
    localStorage.clear();
    navigate("/sign-in");
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <CoverLayout
      sx={{
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
      }}
    >
      {isLoading ? (
        <Stack alignItems="center">
          <CircularProgress
            sx={{
              position: "fixed",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 9,
            }}
            color="info"
            size="10vh"
          />
        </Stack>
      ) : (
        ""
      )}
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="secondary"
          mx={2}
          mt={-3}
          py={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h3" fontWeight="medium" color="white" mt={1}>
            Check your email
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox borderRadius="lg" mx={2} textAlign="center">
            <MDTypography variant="h5" fontWeight="medium" color="black" mt={1}>
              Please enter the verification code we sent to: {loginUser.email}
            </MDTypography>
          </MDBox>

          <Formik
            enableReinitialize
            validateOnChange
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={onSubmit}
          >
            {({ setFieldValue, dirty, errors, isValid }) => (
              <Form>
                <MDBox
                  sx={{
                    "& .MuiFormHelperText-root": { color: "#d32f2f" },
                  }}
                >
                  <MDBox
                    mb={2}
                    sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 3 }}
                  >
                    <OTPInput
                      autoFocus
                      isNumberInput
                      length={6}
                      className="otpContainer"
                      inputClassName="otpInput"
                      style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                      divideIndex={[2]}
                      onChangeOTP={(otp) => {
                        setFieldValue("otp", otp);
                      }}
                    />
                  </MDBox>
                  {errors?.otp ? (
                    <FormHelperText style={{ textAlign: "center" }}>{errors.otp}</FormHelperText>
                  ) : null}
                  <MDBox mt={4} mb={1}>
                    <MDButton
                      disabled={isLoading || !dirty || !isValid}
                      color="primaryRed"
                      fullWidth
                      type="submit"
                    >
                      submit
                    </MDButton>
                  </MDBox>
                  <MDBox borderRadius="lg" mx={2} mt={1} textAlign="center">
                    <MDTypography variant="caption" fontWeight="medium" color="black" mt={1}>
                      Code will expire in 7 days. Didn&apos;t get the email?{" "}
                      <MDTypography
                        variant="span"
                        style={{
                          textDecoration: "underline",
                          ...(disabled ? {} : { cursor: "pointer" }),
                        }}
                        onClick={handleResendCode}
                      >
                        Resend {disabled && <span> ({formatTime(remainingTime)})</span>}
                      </MDTypography>
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    <MDTypography
                      align="center"
                      display="block"
                      style={{ textDecoration: "underline", cursor: "pointer" }}
                      variant="button"
                      my={1}
                      onClick={handleLogout}
                    >
                      Return to log in
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Form>
            )}
          </Formik>
        </MDBox>
      </Card>
    </CoverLayout>
  );
}

export default EmailVerification;
